import { syntaxTree } from '@codemirror/language';
import { EditorState } from '@codemirror/state';

import { escapeSuggestion } from './escape-suggestion.js';
import { getStatementStatus } from './get-statement-status.js';
import { getTextFromRange } from './get-text.js';
import {
  getStatementCursorAtPosition,
  hasNextSibling,
  hasNextStatement,
} from './tree-navigation.js';
import { isLogicalOperator } from './tree-node-guards.js';
import { filterFieldLanguageExtension } from '../parser/filterfield-language.js';
import type { FilterFieldSuggestionProps } from '../suggestions/Suggestion.types.js';
import type { FilterFieldSuggestionsContext } from '../suggestions/suggestions-extension.js';
import type {
  FilterFieldNodeBase,
  FilterFieldStatementNode,
  FilterFieldValueNode,
} from '../types/tree-nodes.js';

/** @internal */
export function getSuggestionTransaction(
  suggestionValue: string,
  range: FilterFieldNodeBase['range'] | undefined,
  shouldEscapeSuggestion: boolean | undefined,
  trailing: string | undefined,
  insertionStrategy: FilterFieldSuggestionProps['insertionStrategy'],
  cursorBeforeTrailing: boolean | undefined,
  suggestionsContext: FilterFieldSuggestionsContext,
  editorState: EditorState,
) {
  const { token, currentStatement: _currentStatement } = suggestionsContext;
  const currentSelection = editorState.selection;
  const { head } = currentSelection.main;

  let currentStatement = _currentStatement;
  if (!currentStatement) {
    currentStatement = {
      type: 'Statement',
      range: { from: token.range.from, to: token.range.to },
    };
  }

  const autoEscapeSuggestion =
    (insertionStrategy === 'replace-token' &&
      shouldEscapeSuggestion === undefined) ||
    shouldEscapeSuggestion;

  const currentDocumentLength = editorState.doc.toString().length;

  const isWildcardSuggestionWithValuePlaceholder = trailing?.includes('value');
  const isInclusionOperatorSuggestion = ['in (', 'not in ('].includes(
    suggestionValue,
  ); // these values come from our defined defaultComparisonOperatorSuggestions values when we have autosuggestions
  const isSuggestionWithWildcard = ['= *', '!= *'].includes(suggestionValue);

  const nextStatement = hasNextStatement(head, editorState);

  // Handle insertion of the inclusion operator suggestion separately,
  // as we have to take care of potential existing values and brackets and
  // always want to place the cursor right before the closing bracket so users
  // can start/continue typing values.
  if (isInclusionOperatorSuggestion && range === undefined) {
    return getSuggestionTransactionForInclusionOperatorSuggestion(
      suggestionValue,
      currentStatement,
      token,
      trailing,
      head,
      currentDocumentLength,
      editorState,
      nextStatement,
    );
  }

  const appliedSuggestionValue = `${
    autoEscapeSuggestion && !isWildcardSuggestionWithValuePlaceholder
      ? escapeSuggestion(suggestionValue)
      : suggestionValue
  }${trailing ?? ''}`;

  const trimmedValue = appliedSuggestionValue.trimEnd();
  const isExistsOperator = trimmedValue.match(/^(= \*)$|^(!= \*)$/);
  const addSpace = appliedSuggestionValue.slice(-1) === ' ';

  let insertedValue = appliedSuggestionValue;
  let replaceFrom = token.range.from;
  let replaceUntil = token.range.to;
  let nextSibling = false;
  let isLastListValue = false;
  let startsWithOperator = false;
  let endsWithOperator = false;
  let hasSpaceAfterQuotes = false;

  const followedByOpeningParenthesis = _isFollowedByOpeningBracket(
    editorState,
    head,
  );

  // If the token at the cursor position is a list, only replace single list values if the cursor
  // is positioned inside the list's brackets.
  if (
    token.type === 'List' &&
    head > token.range.from &&
    head < token.range.to &&
    insertionStrategy === 'replace-token'
  ) {
    replaceFrom = head;
    replaceUntil = head;
  }

  const { value } = currentStatement ?? {};
  const isValueToken =
    token.range.from === value?.range.from &&
    token.range.to === value?.range.to;

  // If the current statement is undefined, it is definitely not followed by any
  // other node and we want to automatically add the default trailing space
  // (unless overridden) when applying a suggestion.
  // If there is a statement, we want to override the added space in certain cases.
  if (currentStatement) {
    nextSibling = !nextStatement
      ? hasNextSibling(head, editorState, { ignorePositionOperators: true })
      : nextStatement;
    startsWithOperator =
      isValueToken &&
      currentStatement.comparisonOperator?.type === 'ComparisonOperator' &&
      (currentStatement.comparisonOperator.value === 'starts-with' ||
        currentStatement.comparisonOperator.value === 'contains');
    endsWithOperator =
      isValueToken &&
      currentStatement.comparisonOperator?.type === 'ComparisonOperator' &&
      (currentStatement.comparisonOperator.value === 'ends-with' ||
        currentStatement.comparisonOperator.value === 'contains');

    if (nextSibling && insertionIsLastListValue(head, editorState)) {
      // We need this flag to not move the cursor outside of the brackets when
      // adding a value as the last value in a list. (e.g. a in (a|))
      isLastListValue = true;
    }

    // If the applied suggestion is an exists operator (= * | != *), we want to also
    // replace the value of the current filter statement.
    // e.g.:
    //   foo =| bar
    //   insert "= *"
    //   foo = *
    if (
      insertionStrategy === 'replace-statement' ||
      (isExistsOperator &&
        currentStatement.value &&
        !followedByOpeningParenthesis) ||
      startsWithOperator
    ) {
      nextSibling = nextStatement;
      replaceUntil = currentStatement.range.to;
    }

    insertedValue =
      nextSibling || insertionStrategy === 'insert'
        ? trimmedValue
        : appliedSuggestionValue;
    const containsOperator = startsWithOperator && endsWithOperator;

    // When replacing a value that has a starts-with operator, we want to replace
    // the value while keeping the operator. If a space is supposed to be added, we
    // have to add it after the position operator.
    if (
      (startsWithOperator || endsWithOperator) &&
      insertionStrategy === 'replace-token'
    ) {
      let { from, to } = {
        from: currentStatement.range.to,
        to: currentStatement.range.to,
      };

      if (currentStatement.value) {
        ({ from, to } = currentStatement.value.range);
      }

      if (
        startsWithOperator &&
        ((head > from && head < to) || (!containsOperator && head === from))
      ) {
        startsWithOperator = false;
        if (!trimmedValue.endsWith('*')) {
          insertedValue =
            containsOperator && !trimmedValue.startsWith('*')
              ? `*${trimmedValue}*`
              : `${trimmedValue}*`;

          insertedValue = `${insertedValue}${
            !nextSibling && addSpace ? ' ' : ''
          }`;
        }
      } else {
        if (head === from || (startsWithOperator && head === to)) {
          replaceFrom = from;
          insertedValue = appliedSuggestionValue;
        }

        replaceUntil = to;
        startsWithOperator = false;
        nextSibling = nextStatement;
      }
    }
  } else {
    insertedValue = nextSibling ? trimmedValue : appliedSuggestionValue;
  }

  let insertionRange: { from: number; to: number };

  switch (insertionStrategy) {
    case 'insert':
      insertionRange = {
        from: head,
        to: head,
      };
      break;
    case 'replace-statement': {
      const editorState = EditorState.create({
        doc: appliedSuggestionValue,
        extensions: [filterFieldLanguageExtension],
      });
      const suggestionCursor = getStatementCursorAtPosition(
        appliedSuggestionValue.length,
        editorState,
      );

      if (getStatementStatus(suggestionCursor) !== 'complete') {
        insertedValue = appliedSuggestionValue;
        nextSibling = false;
      }

      insertionRange = {
        from: Math.min(
          currentDocumentLength,
          currentStatement?.range.from ?? head,
        ),
        to: Math.min(currentDocumentLength, currentStatement?.range.to ?? head),
      };

      if (isLogicalOperator(token)) {
        nextSibling = nextStatement;
        if (nextSibling) {
          insertedValue = trimmedValue;
        }

        insertionRange = {
          from: Math.min(currentDocumentLength, token.range.from),
          to: Math.min(currentDocumentLength, token.range.to),
        };
      }
      break;
    }
    case 'replace-all':
      // Reset inserted value to the initial suggestion value, including all trailing characters.
      insertedValue = appliedSuggestionValue;
      nextSibling = false;
      insertionRange = {
        from: 0,
        to: editorState.doc.toString().length,
      };
      break;
    default: {
      if (
        (token.type === 'String' ||
          token.type === 'Key' ||
          token.type === 'Error') &&
        range === undefined
      ) {
        // The current token may end before the enclosing statement, e.g. '"foo"| ' -> current token '"foo"' doesn't
        // include the space, which is included in the statement's range.
        // We need to check whether there's a space after the current token to properly insert the suggestion.
        if (token.range.to < (currentStatement?.range.to ?? 0)) {
          const characterAfterToken = getTextFromRange(
            {
              from: token.range.to,
              to: token.range.to + 1,
            },
            editorState.doc.toString(),
          );

          // If there's already a space after the current token, handle insertion as if there was a next sibling.
          if (characterAfterToken === ' ') {
            nextSibling = true;
            insertedValue = trimmedValue;
          }
        }

        if (head > token.range.from && head < token.range.to) {
          const isTokenEscaped = token.textValue.match(/^"[^"]*"$/);
          const isAppliedSuggestionValueEscaped =
            appliedSuggestionValue.match(/^"[^"]*"$/) ??
            appliedSuggestionValue.match(/^"[^"]*" $/);

          if (isTokenEscaped && !isAppliedSuggestionValueEscaped) {
            replaceFrom = token.range.from + 1;
            replaceUntil = token.range.to;

            const addSpace = insertedValue.slice(-1) !== ' ';

            insertedValue = addSpace ? `${trimmedValue}" ` : `${trimmedValue}"`;
            hasSpaceAfterQuotes = addSpace;
          }
        }
      } else if (range) {
        ({ from: replaceFrom, to: replaceUntil } = range);
      }

      insertionRange = {
        from: Math.min(currentDocumentLength, replaceFrom),
        to: Math.min(currentDocumentLength, replaceUntil),
      };
    }
  }

  let anchor =
    insertionStrategy === 'insert'
      ? head + insertedValue.length
      : Math.min(currentDocumentLength, insertionRange.from) +
        (nextSibling && !isLastListValue
          ? appliedSuggestionValue.length
          : insertedValue.length) +
        (insertionStrategy === 'replace-token' ? +startsWithOperator : 0) +
        +hasSpaceAfterQuotes;

  // When a custom suggestion with trailing `value` is provided, we automatically adjust the selection
  // so the placeholder is selected to have the same behaviour as for ootb wildcard suggestions.
  if (cursorBeforeTrailing || isWildcardSuggestionWithValuePlaceholder) {
    const trailingLength = trailing ? trailing.length : 0;
    anchor -=
      trailingLength +
      (autoEscapeSuggestion &&
      !(isSuggestionWithWildcard || isWildcardSuggestionWithValuePlaceholder)
        ? 1
        : 0);
  }

  // Additional safeguard:
  // Clamp anchor to be within the new document length to prevent
  // "Selection points outside of document" errors.
  const newDocLength =
    currentDocumentLength -
    (insertionRange.to - insertionRange.from) +
    insertedValue.length;
  anchor = Math.max(0, Math.min(anchor, newDocLength));

  insertionRange = getSortedRange(insertionRange);

  return {
    changes: {
      ...insertionRange,
      insert: insertedValue,
    },
    selection: {
      anchor,
      // Select the value placeholder if inserting a wildcard suggestion with a placeholder
      ...(isWildcardSuggestionWithValuePlaceholder ? { head: anchor + 5 } : {}), // 5 = 'value'.length;
    },
  };
}

/**
 * Handles the insertion and cursor position of the inclusion operator
 * suggestions ("in ()" and "not in ()")
 */
function getSuggestionTransactionForInclusionOperatorSuggestion(
  suggestionValue: string,
  currentStatement: FilterFieldStatementNode | undefined,
  token: FilterFieldValueNode,
  trailing: string | undefined,
  head: number,
  currentDocumentLength: number,
  editorState: EditorState,
  nextStatement: boolean | undefined,
) {
  let insertedValue = suggestionValue;

  // If a value already exists we have to take care of potential brackets
  if (currentStatement?.value) {
    let stringifiedValue = currentStatement.value.textValue;

    // we remove potential brackets from the value so that they are not
    // added twice (e.g. "a in (b" should be "a in (b)" not "a in ((b)")
    // (For type List we know that the brackets are present, but the value could be an
    // error node with incomplete / no brackets)
    stringifiedValue = stringifiedValue
      .replace(/^\(/, '') // remove starting opening bracket if present
      .replace(/(?<!\\)\)$/, ''); // only remove a closing bracket if it's not escaped

    if (currentStatement.value.type !== 'List') {
      // if the value is not a list, we also want to clean up any extra
      // spaces that might be present e.g. "a in (b      " -> "a in (b)"
      insertedValue = `${insertedValue}${stringifiedValue.trimEnd()}`;
    } else {
      insertedValue = `${insertedValue}${stringifiedValue}`;
    }
  }

  // We want to insert / reinsert both operator and value
  // as we have to place the brackets before and after the value
  let insertionRange = {
    from: Math.min(
      currentDocumentLength,
      currentStatement?.comparisonOperator?.range.from ?? token.range.from,
    ),
    to: Math.min(
      currentDocumentLength,
      currentStatement?.value?.range.to ?? token.range.to,
    ),
  };

  insertionRange = getSortedRange(insertionRange);
  let anchor = insertionRange.from + insertedValue.length;

  // Add the trailing closing bracket
  insertedValue = `${insertedValue}${trailing}`;

  // Only add a space at the end if there is not yet a space or another statement
  // after the current statement
  const nextSibling = !nextStatement
    ? hasNextSibling(head, editorState, { ignorePositionOperators: true })
    : nextStatement;
  insertedValue = `${insertedValue.trimEnd()}${!nextSibling ? ' ' : ''}`;

  // Additional safeguard:
  // Clamp anchor to be within the new document length to prevent
  // "Selection points outside of document" errors.
  const newDocLength =
    currentDocumentLength -
    (insertionRange.to - insertionRange.from) +
    insertedValue.length;
  anchor = Math.max(0, Math.min(anchor, newDocLength));

  return {
    changes: {
      ...insertionRange,
      insert: insertedValue,
    },
    selection: {
      anchor: anchor,
    },
  };
}

/**
 * Returns the given range with the from and to values sorted so that from is always smaller than to.
 * This is only needed in some edge cases (e.g. when choosing the in operator suggestions here "foo | b"),
 * but to be safe we want sort the range.
 * @internal
 */
function getSortedRange(range: { from: number; to: number }) {
  if (range.to < range.from) {
    return { from: range.to, to: range.from };
  }

  return range;
}

/**
 * Check if the next node after cursor position contains an opening bracket.
 * @internal
 */
export function _isFollowedByOpeningBracket(
  editorState: EditorState,
  head: number,
) {
  const tree = syntaxTree(editorState);

  let cursor = tree.cursorAt(head, 1);

  if (cursor.name === 'OpeningBracket') {
    return true;
  }

  if (cursor.node.name === 'Filter' || cursor.node.name === 'FilterStatement') {
    cursor = tree.cursorAt(head, -1);
  }

  if (!cursor.next()) {
    return false;
  }

  if (cursor.name === 'OpeningBracket' && cursor.node.from >= head) {
    return true;
  }

  if (cursor.name === '⚠') {
    if (cursor.next() && cursor.node.from >= head) {
      // the first check handles cases like `foo = (bar` and the FilterGroup check handles cases like `foo = (bar, baz)`, that already includes an opening bracket
      // @ts-expect-error the cursor name type changed by calling next, but typescript doesn't recognize it
      if (cursor.name === 'OpeningBracket' || cursor.name === 'FilterGroup') {
        return true;
      }
    }
  }

  if (cursor.name === 'FilterIncludesValue') {
    if (cursor.next() && cursor.node.from >= head) {
      // if the node is a list node, we know it already includes an opening bracket and otherwise we check if it's just an opening bracket node
      // @ts-expect-error the cursor name type changed by calling next, but typescript doesn't recognize it
      if (cursor.name === 'OpeningBracket' || cursor.name === 'List') {
        return true;
      }
    }
  }

  // keep track of the cursor at the next position for cases like (a = 1) (a = b) at index 6 or foo = bar (test, bla) at index 8
  const cursorAtExactPosition = tree.cursorAt(head + 1, 1);

  if (cursorAtExactPosition.node.name === 'OpeningBracket') {
    return true;
  }

  cursorAtExactPosition.parent();

  if (
    cursorAtExactPosition.node.name !== 'Filter' &&
    cursor.node.name === 'FilterGroup'
  ) {
    return true;
  }

  return false;
}

/**
 * Check whether the node that is be entered at the given position is the last value in a list.
 * @internal
 */
export function insertionIsLastListValue(
  cursorPosition: number,
  editorState: EditorState,
): boolean {
  const tree = syntaxTree(editorState);
  const cursor = tree.cursorAt(cursorPosition, 1);

  // If the cursor is right before a closing bracket, we definitely have a next sibling.
  if (
    cursor.node.name === 'ClosingBracket' &&
    cursorPosition === cursor.node.from
  ) {
    if (cursor.parent() && cursor.name === 'List') {
      return true;
    }
  }

  return false;
}
